import React, { useEffect, useRef } from 'react';
import { Image } from 'react-konva';

import { getElementNodeId } from '../../core/documentSchema';
import { CssImageFilter, hasActiveFilters } from './filters';

const FILTER_CACHE_DEBOUNCE_MS = 180;
const PREVIEW_CACHE_MAX_EDGE = 1280;

function ImageElement({
  element,
  interactive,
  listening = true,
  cacheMaxEdge = PREVIEW_CACHE_MAX_EDGE,
  filterDebounceMs = FILTER_CACHE_DEBOUNCE_MS,
  onSelect,
  onContextMenu,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransformEnd,
}) {
  const nodeRef = useRef(null);
  const { filters } = element;
  const active = hasActiveFilters(filters);
  const crop = element.crop;
  const { naturalWidth, naturalHeight } = element;

  const applyFilterCache = () => {
    const node = nodeRef.current;
    if (!node || !element.image) {
      return;
    }

    if (active) {
      const edge = Math.max(node.width() || 1, node.height() || 1);
      const pixelRatio = Number.isFinite(cacheMaxEdge) ? Math.min(1, cacheMaxEdge / edge) : 1;
      const blur = filters.blur || 0;
      node.setAttr('msFilters', {
        ...filters,
        blur: blur * pixelRatio,
      });
      node.filters([CssImageFilter]);
      try {
        node.cache({ pixelRatio });
      } catch {
        node.filters([]);
        node.clearCache();
      }
    } else {
      node.filters([]);
      node.clearCache();
    }
    node.getLayer()?.batchDraw();
  };

  useEffect(() => {
    applyFilterCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.image, element.width, element.height, element.crop, active, cacheMaxEdge]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    if (!filterDebounceMs) {
      applyFilterCache();
      return undefined;
    }

    const timer = window.setTimeout(applyFilterCache, filterDebounceMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.blur,
    filters.brightness,
    filters.contrast,
    filters.saturation,
    filters.temperature,
    filterDebounceMs,
    cacheMaxEdge,
  ]);

  if (!element.visible || !element.image) {
    return null;
  }

  return (
    <Image
      ref={nodeRef}
      id={getElementNodeId(element.id)}
      image={element.image}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      offsetX={element.width / 2}
      offsetY={element.height / 2}
      scaleX={element.scaleX}
      scaleY={element.scaleY}
      rotation={element.rotation}
      opacity={element.opacity}
      globalCompositeOperation={element.blendMode}
      crop={
        crop
          ? {
              x: crop.x,
              y: crop.y,
              width: crop.width,
              height: crop.height,
            }
          : undefined
      }
      cropWidth={crop ? crop.width : naturalWidth}
      cropHeight={crop ? crop.height : naturalHeight}
      listening={listening}
      draggable={interactive}
      onClick={(event) => onSelect?.(element.id, event)}
      onTap={(event) => onSelect?.(element.id, event)}
      onContextMenu={(event) => onContextMenu?.(element, event)}
      onDragStart={() => onDragStart?.(element.id)}
      onDragMove={interactive ? (event) => onDragMove?.(element, event) : undefined}
      onDragEnd={(event) => onDragEnd?.(element.id, event)}
      onTransformEnd={(event) => onTransformEnd?.(element.id, event)}
    />
  );
}

export default ImageElement;
